import { AuthService } from 'shared/lib/keycloak';
import { Org } from 'shared/api/onboarding';
import { getAmpConfig } from 'shared/config/amp-config';

import { EVENT_TYPES } from './constants';
import { PrechatData } from './types';

const getIPFromAmazon = async () => {
  const response = await fetch('https://checkip.amazonaws.com/');
  const result = await response.text();

  return result.replace(/\n/g, '');
};

const createHundredCharStringFromLists = (config: Record<string, string[]>) => {
  const defaultConfig: Record<string, string> = {
    schoolIds: '',
    schoolNames: '',
    districtIds: '',
    districtNames: '',
  };

  return Object.entries(config).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = value.join(',').slice(0, 99);
    }

    return acc;
  }, defaultConfig);
};

export const getPrechatData = ({
  schools,
  districts,
}: {
  schools: Org[];
  districts: Org[];
}) => {
  const districtIds = districts.map((distr) => distr.sourcedId);
  const districtNames = districts.map((distr) => distr.name);

  const schoolIds = schools.map((distr) => distr.sourcedId);
  const schoolNames = schools.map((distr) => distr.name);

  return { districtIds, districtNames, schoolIds, schoolNames };
};

const getPrechatFields = async (prechatData: PrechatData) => {
  const userProfile = AuthService.getUserProfile();
  const referralUrl = window.location.href || '';
  const ipAddress = await getIPFromAmazon();
  const userBk = AuthService.getUserBk();

  const { schoolIds, schoolNames, districtIds, districtNames } =
    createHundredCharStringFromLists(prechatData);

  const params = {
    email: userProfile.email ?? 'Unknown User',
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    referral_url: referralUrl,
    ip_address: ipAddress,
    products: 'Educator Home',
    amplify_id: userBk,
    school_id: schoolIds,
    'school_id name': schoolNames,
    district_id: districtIds,
    district: districtNames,
  };

  return params;
};

const initEmbeddedMessaging = async (
  prechatData: PrechatData,
  callback?: () => void,
) => {
  try {
    const identityToken = AuthService.getAccessToken();
    const prechatFields = await getPrechatFields(prechatData);
    const { chatBotAddress, chatBotLabel, salesforceOrgId, salesforceOrgCert } =
      getAmpConfig().salesforceChat;

    window.embeddedservice_bootstrap.settings.language = 'en_US';

    window.addEventListener(EVENT_TYPES.ON_READY, () => {
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(
        prechatFields,
      );

      window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken,
      });

      callback?.();
    });

    window.addEventListener(EVENT_TYPES.ON_TOKEN_EXPIRED, () => {
      window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken,
      });
    });

    window.embeddedservice_bootstrap.init(
      salesforceOrgId,
      chatBotLabel,
      chatBotAddress,
      {
        scrt2URL: salesforceOrgCert,
      },
    );
  } catch (err) {
    console.error('SF Chat: Error loading script.', err);
  }
};

export const createSfScriptTag = (
  prechatData: PrechatData,
  callback?: () => void,
) => {
  const { scriptSrc } = getAmpConfig().salesforceChat;

  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;

  document.body.appendChild(script);

  // Callback function to run when script is loaded
  script.onload = () => initEmbeddedMessaging(prechatData, callback);

  return script;
};
